/* HeroBanner — Cinematic hero section card with inline editing */

import InlineTextEditor from '@/components/cms/InlineTextEditor'

interface HeroBannerProps {
  data: Record<string, string>
  onUpdateData?: (updates: Record<string, string>) => void
}

export default function HeroBanner({ data, onUpdateData }: HeroBannerProps) {
  const headline = data.headline || 'Your Headline Here'
  const subtitle = data.subtitle || 'A brief description of your business'
  const ctaText = data.ctaText || 'Get Started'
  const bgImage = data.bgImage || ''

  return (
    <div data-keep-white data-testid="card-hero-banner" className="relative overflow-hidden rounded-xl min-h-[280px] flex items-center justify-center">
      {/* Background layer */}
      {bgImage ? (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${bgImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        </div>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a1a] via-[#12122e] to-[#0d0d2b]">
          {/* Decorative mesh gradient */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-blue-500/10 blur-[80px]" />
            <div className="absolute bottom-1/4 right-1/4 w-48 h-48 rounded-full bg-purple-500/10 blur-[60px]" />
          </div>
          {/* Grain overlay */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }} />
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 text-center px-8 py-12 max-w-2xl mx-auto">
        {onUpdateData ? (
          <InlineTextEditor
            value={headline}
            onChange={(v) => onUpdateData({ headline: v })}
            variant="heading"
            placeholder="Your Headline Here"
            className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight tracking-tight"
          />
        ) : (
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight tracking-tight">
            {headline}
          </h1>
        )}

        {onUpdateData ? (
          <InlineTextEditor
            value={subtitle}
            onChange={(v) => onUpdateData({ subtitle: v })}
            variant="body"
            placeholder="A brief description of your business"
            className="text-base text-white/50 mb-8 leading-relaxed max-w-lg mx-auto"
          />
        ) : (
          <p className="text-base text-white/50 mb-8 leading-relaxed max-w-lg mx-auto">
            {subtitle}
          </p>
        )}

        <button className="
          inline-flex items-center gap-2 px-6 py-3 rounded-xl
          bg-white text-[#0a0a1a] text-sm font-semibold
          shadow-[0_0_30px_rgba(255,255,255,0.1)]
          hover:shadow-[0_0_40px_rgba(255,255,255,0.2)]
          hover:-translate-y-0.5
          transition-[opacity,transform] duration-300
        ">
          {ctaText}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </button>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#0c0c18] to-transparent" />
    </div>
  )
}
